import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { Shape } from '../../types/Shape';
import { Annotation, AnnotationPoint } from '../../types/Annotation';
import HTMLShapeBase from './HTMLShapeBase';
import AnnotationCanvas from '../annotation/AnnotationCanvas';
import AnnotationToolbar from '../annotation/AnnotationToolbar';
import { useAnnotation } from '../../hooks/useAnnotation';

interface HTMLImageProps {
  shape: Shape;
  isSelected: boolean;
  showResizeHandles?: boolean;
  stagePos: { x: number; y: number };
  stageScale: number;
  isDrawing?: boolean;
  isEditing?: boolean;
  currentTool?: string;
  onDragStart?: (shapeId: string) => void;
  onDragMove?: (shapeId: string, newX: number, newY: number) => void;
  onDragEnd?: (shapeId: string) => void;
  onResize?: (shapeId: string, newWidth: number, newHeight: number, newX?: number, newY?: number) => void;
  onResizeEnd?: () => void;
  onClick?: (shapeId: string, e: React.MouseEvent) => void;
  onDoubleClick?: (shapeId: string, e: React.MouseEvent) => void;
  onContextMenu?: (shapeId: string, e: React.MouseEvent) => void;
  onMouseEnter?: (shapeId: string) => void;
  onMouseLeave?: () => void;
  onStageClick?: (canvasX: number, canvasY: number, e: React.MouseEvent) => void;
  /** Annotation 업데이트 콜백 */
  onAnnotationUpdate?: (shapeId: string, annotations: Annotation[]) => void;
}

const HTMLImage: React.FC<HTMLImageProps> = (props) => {
  const {
    shape,
    stageScale,
    isSelected,
    onAnnotationUpdate,
  } = props;

  // Annotation 모드 상태
  const [isAnnotationMode, setIsAnnotationMode] = useState(false);
  // 툴바 위치 (클릭한 마우스 위치 기준)
  const [toolbarPosition, setToolbarPosition] = useState<{ x: number; y: number } | null>(null);

  // 이미지 실제 렌더링 크기 (Canvas 크기 동기화용)
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  // Annotation 훅 사용
  const annotation = useAnnotation({
    initialAnnotations: shape.annotations || [],
    onAnnotationsChange: (annotations) => {
      if (onAnnotationUpdate) {
        onAnnotationUpdate(shape.id, annotations);
      }
    },
  });

  // 외부 annotations 변경 시 동기화
  useEffect(() => {
    if (shape.annotations && shape.annotations !== annotation.state.annotations) {
      annotation.setAnnotations(shape.annotations);
    }
  }, [shape.annotations]);

  // 이미지 크기 추적
  useEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setImageSize({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    // 초기 크기 설정
    updateSize();

    // ResizeObserver로 크기 변경 감지
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // 클릭 핸들러 (선택/드래그만 — 어노테이션 모드 진입 안 함)
  const handleClick = useCallback(
    (shapeId: string, e: React.MouseEvent) => {
      if (isAnnotationMode) return;
      if (props.onClick) {
        props.onClick(shapeId, e);
      }
    },
    [isAnnotationMode, props.onClick]
  );

  // 더블클릭 핸들러 (annotation 모드 활성화)
  const handleDoubleClick = useCallback(
    (shapeId: string, e: React.MouseEvent) => {
      if (isAnnotationMode) return;

      // 컨테이너 기준 상대 위치 계산
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const relativeX = e.clientX - containerRect.left;
        const relativeY = e.clientY - containerRect.top;
        setToolbarPosition({ x: relativeX, y: relativeY });
      }

      setIsAnnotationMode(true);
      annotation.setTool('none');

      if (props.onDoubleClick) {
        props.onDoubleClick(shapeId, e);
      }
    },
    [isAnnotationMode, annotation, props.onDoubleClick]
  );

  // Annotation 모드 종료
  const handleCloseAnnotation = useCallback(() => {
    setIsAnnotationMode(false);
    setToolbarPosition(null);
    annotation.setTool('none');
  }, [annotation]);

  // 형광펜 추가 핸들러
  const handleHighlighterAdd = useCallback(
    (points: AnnotationPoint[]) => {
      annotation.addHighlighter(points);
    },
    [annotation]
  );

  // 밑줄 추가 핸들러
  const handleUnderlineAdd = useCallback(
    (startPoint: AnnotationPoint, endPoint: AnnotationPoint) => {
      annotation.addUnderline(startPoint, endPoint);
    },
    [annotation]
  );

  // Annotation 삭제 핸들러
  const handleAnnotationDelete = useCallback(
    (id: string) => {
      annotation.deleteAnnotation(id);
    },
    [annotation]
  );

  // ESC 키 또는 이미지 밖 클릭으로 annotation 모드 종료
  useEffect(() => {
    if (!isAnnotationMode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseAnnotation();
      } else if (e.key === 'h' || e.key === 'H') {
        annotation.setTool('highlighter');
      } else if (e.key === 'u' || e.key === 'U') {
        annotation.setTool('underline');
      } else if (e.key === 'e' || e.key === 'E') {
        annotation.setTool('eraser');
      }
    };

    // 이미지 밖 클릭 시 annotation 모드 종료
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleCloseAnnotation();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    // mousedown으로 해야 다른 요소 클릭 시 바로 감지됨
    window.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAnnotationMode, handleCloseAnnotation, annotation]);

  // Canvas 크기 계산 (border 제외)
  const borderWidth = (shape.strokeWidth || 1) * stageScale;
  const canvasWidth = Math.max(0, imageSize.width - borderWidth * 2);
  const canvasHeight = Math.max(0, imageSize.height - borderWidth * 2);

  return (
    <HTMLShapeBase
      {...props}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      isAnnotationMode={isAnnotationMode}
    >
      {/* Annotation Toolbar - 클릭한 위치 기준 50px 위에 배치 */}
      {isAnnotationMode && isSelected && toolbarPosition && (
        <AnnotationToolbar
          currentTool={annotation.state.currentTool}
          currentColor={annotation.state.currentColor}
          currentStrokeWidth={annotation.state.currentStrokeWidth}
          currentOpacity={annotation.state.currentOpacity}
          currentUnderlineStyle={annotation.state.currentUnderlineStyle}
          onToolChange={annotation.setTool}
          onColorChange={annotation.setColor}
          onStrokeWidthChange={annotation.setStrokeWidth}
          onOpacityChange={annotation.setOpacity}
          onUnderlineStyleChange={annotation.setUnderlineStyle}
          onClearAll={annotation.clearAnnotations}
          onClose={handleCloseAnnotation}
          stageScale={stageScale}
          position={toolbarPosition}
        />
      )}

      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          border: `${borderWidth}px solid ${shape.stroke || '#D1D5DB'}`,
          borderRadius: `${4 * stageScale}px`,
          boxSizing: 'border-box',
          overflow: 'hidden',
          backgroundColor: '#f9fafb',
          lineHeight: 0,
          fontSize: 0,
          position: 'relative',
          pointerEvents: isAnnotationMode ? 'auto' : 'none',
        }}
      >
        {/* 이미지 */}
        {shape.imageDataUrl && (
          <img
            src={shape.imageDataUrl}
            alt="Uploaded"
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'fill',
              pointerEvents: 'none',
              verticalAlign: 'top',
              margin: 0,
              padding: 0,
            }}
          />
        )}

        {/* Annotation Canvas (항상 표시, 편집은 annotation 모드에서만) */}
        {canvasWidth > 0 && canvasHeight > 0 && (
          <AnnotationCanvas
            width={canvasWidth}
            height={canvasHeight}
            annotations={annotation.state.annotations}
            isEditing={isAnnotationMode}
            currentTool={annotation.state.currentTool}
            currentColor={annotation.state.currentColor}
            currentStrokeWidth={annotation.state.currentStrokeWidth}
            currentOpacity={annotation.state.currentOpacity}
            currentUnderlineStyle={annotation.state.currentUnderlineStyle}
            onHighlighterAdd={handleHighlighterAdd}
            onUnderlineAdd={handleUnderlineAdd}
            onAnnotationDelete={handleAnnotationDelete}
            stageScale={stageScale}
          />
        )}
      </div>
    </HTMLShapeBase>
  );
};

export default memo(HTMLImage);
